import { useCallback, useMemo, useRef, useState } from 'react'
import Head from 'next/head'
import ErrorComponent from 'next/error'
import { useQuery } from '@tanstack/react-query'
import Link from 'next/link'
import Router from 'next/router'
import {
  getEnabledConciergeLocations,
  getEnabledConciergeLocationsKey,
} from '../../api-client/listing'
import { AuctionStatus, OfferStatus, VehicleStatus, VehicleType } from '../../api-client/enums'

import { Desktop, Mobile } from '../../components/media'
import Loader from '../../components/loader'
import Gallery from '../../components/gallery'
import AuctionStatusBadge from '../../components/auction-status-badge'
import NegotiationModal from '../../components/negotiation-modal'
import MakeAnOfferModal from '../../components/make-an-offer-modal'
import BuyNowModal from '../../components/buy-now-modal'
import OfferReceivedModal from '../../components/offer-received-modal'
import { LinkButton } from '../../components/button'
import useNegotiationInfo from '../../components/negotiation-modal/use-negotiation-info'
import Location from '../../components/location'

import AuctionInfo from '../../containers/vehicle/auction-card/auction-info'
import Deals from '../../containers/vehicle/auction-card/deals'
import LotInfo from '../../containers/vehicle/auction-card/lot-info'
import Seller from '../../containers/vehicle/auction-card/seller'
import BidInput from '../../containers/vehicle/auction-card/bid-input'
import Navbar from '../../containers/vehicle/navbar'
import Details from '../../containers/vehicle/details'
import Payments from '../../containers/vehicle/payment'
import Ownership from '../../containers/vehicle/ownership'
import Inspection from '../../containers/vehicle/inspection'
import BidderBadge from '../../containers/vehicle/auction-card/bidder-badge'
import CTACards from '../../containers/vehicle/cta-section'
import useVehiclePageData from '../../containers/vehicle/use-vehicle-page-data'
import StatusText from '../../containers/vehicle/auction-card/status-text'
import HistoryReport from '../../containers/vehicle/auction-card/history-report'
import MembershipInfo from '../../containers/vehicle/auction-card/membership-info'
import useSignalr from '../../containers/vehicle/use-signalr'
import BuyNowInfo from '../../containers/vehicle/auction-card/buy-now-info'
import OfferMadeLabel from '../../containers/vehicle/auction-card/offer-made-label'
import CannotBuyYourVehicle from '../../containers/vehicle/modals/cannot-buy-your-vehicle'
import BuyFeeAgreement from '../../containers/vehicle/modals/buy-fee-agreement'
import GetVerified from '../../containers/vehicle/modals/get-verified'
import { useAuth } from '../../contexts/auth'
import { ROUTES, zIndex } from '../../constants'
import { GA_TRACKING_ID2 } from '../../utils/g-tag'
import getClosestLocation from '../../utils/get-closest-location'
import Checkmark from '../../components/svg/checkmark'
import { differenceInSeconds, now, parseISO } from '../../utils/date-service'
import useMemberships from '../../hooks/use-memberships'
import { wrapURLs } from '../../utils/wrap-url'

// ----------------------------------------------------------------------

const MODALS = {
  None: 0,
  MakeAnOfferModal: 1,
  NegotiationModal: 2,
  BuyNowModal: 3,
  CannotBuyYourOwnVehicle: 4,
  BuyFeeAgreement: 5,
  GetVerified: 6,
  OfferReceived: 7,
}

export const TARGETS = {
  Offer: 'offer',
  BuyNow: 'buyNow',
}

// ----------------------------------------------------------------------

const Container = ({ children }) => (
  <div className='container'>
    {children}
    <style jsx>{`
      .container {
        max-width: 194rem;
        width: 100%;
        margin: 0 auto;
        padding: 0 4rem;
      }

      @media (max-width: 1024px) {
        .container {
          padding: 0 1.2rem;
        }
      }
    `}</style>
  </div>
)

const getEndedAuctionStatusText = ({ reserveIsMet, currentUserIsTopBidder }) => {
  if (reserveIsMet) {
    if (currentUserIsTopBidder) {
      return (
        <span>
          Congratulations you have bought this vehicle. Press Continue button to complete your
          purchase.
        </span>
      )
    }

    return (
      <span>
        This auction has ended.
        <br />
        The vehicle has been sold to the user with the highest bid.
      </span>
    )
  }

  return <span>This vehicle didn't meet reserve.</span>
}

const AuctionCardDetailsStatus = ({ auctionInfo }) => {
  switch (auctionInfo.auctionStatus) {
    case AuctionStatus.Stopped:
      return <StatusText>Auction has been paused</StatusText>

    case AuctionStatus.Ended:
      return (
        <>
          <AuctionInfo />
          <StatusText>{getEndedAuctionStatusText(auctionInfo)}</StatusText>
        </>
      )

    default:
      return (
        <>
          <AuctionInfo />
          <Deals />
        </>
      )
  }
}

const AuctionCardDetails = ({ auctionInfo }) => {
  return (
    <>
      <BidderBadge />
      <AuctionCardDetailsStatus auctionInfo={auctionInfo} />
    </>
  )
}

const getBuyItNowStatusText = ({ buyNowListingInfo }) => {
  if (buyNowListingInfo?.sellDetails?.soldAt) {
    return <span>This vehicle has been sold and is no longer available.</span>
  }

  return (
    <span>This listing has expired. Check back again later to see if the seller re-lists it.</span>
  )
}

const BuyItNowCardDetails = ({
  onBuyNowClick,
  onMakeOfferClick,
  status,
  directOfferExists,
  buyNowListingInfo,
}) => {
  return (
    <>
      {buyNowListingInfo?.sellDetails?.soldAt ? (
        <StatusText>{getBuyItNowStatusText({ buyNowListingInfo })}</StatusText>
      ) : (
        <>
          <BuyNowInfo
            onBuyNowClick={onBuyNowClick}
            onMakeOfferClick={onMakeOfferClick}
            status={status}
            directOfferExists={directOfferExists}
          />
          <Deals />
        </>
      )}
    </>
  )
}

const ContinueButton = ({ auctionId }) => {
  return (
    <div className='root flex justifyCenter'>
      <LinkButton href={`${ROUTES.PurchaseAuction}/${auctionId}`}>Continue</LinkButton>

      <style jsx>{`
        .root {
          margin-bottom: 1rem;
        }

        @media (max-width: 1024px) {
          .root {
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  )
}

const DealerInventoryTypeMark = () => {
  return (
    <div className='root'>
      <span style={{ marginRight: '1rem' }}>Dealer Vehicle</span>
      <Checkmark height={16} width={16} fill='var(--primary-button-color)' />

      <style jsx>{`
        .root {
          position: absolute;
          bottom: 0;
          left: 0;
          display: flex;
          justify-content: center;
          width: 100%;
          height: 3rem;
          background: var(--success-color);
          align-items: center;
          border-bottom-left-radius: 1rem;
          border-bottom-right-radius: 1rem;
          color: white;
          font-size: 1.4rem;
          font-weight: 700;
        }

        @media (max-width: 1024px) {
          .root {
            position: initial;
            border-bottom-left-radius: unset;
            border-bottom-right-radius: unset;
            font-size: 1.2rem;
            height: 2.5rem;
          }
        }
      `}</style>
    </div>
  )
}

const VerifiedDealerOption = () => {
  return (
    <div className='root flex alignCenter'>
      <Checkmark
        height={18}
        width={18}
        fill='var(--success-color)'
        style={{ marginRight: '1rem' }}
      />
      <span className='label'>Verified Dealer</span>

      <style jsx>{`
        .root {
          flex: 1 0 auto;
        }
        .label {
          font-size: 1.6rem;
          font-weight: bold;
        }

        @media (max-width: 1024px) {
          .root {
            display: none;
          }
          .label {
            font-size: 1.4rem;
          }
        }
      `}</style>
    </div>
  )
}

const VehicleDetailsSideCardDesktop = ({
  auctionInfo,
  directOfferExists,
  status,
  buyNowListingInfo,
  vehicleData,
  isAuthenticated,
  sellerConciergeLocation,
  onBuyNowClick,
  onMakeOfferClick,
  isDealerInventoryVehicleType,
  sellerInfo,
  shipmentRate,
  isUserOwner,
}) => {
  return (
    <div className='root'>
      <div className='shadowContainer'>
        <LotInfo sellerConciergeLocation={sellerConciergeLocation} shipmentRate={shipmentRate} />

        {auctionInfo && <AuctionCardDetails auctionInfo={auctionInfo} />}

        {buyNowListingInfo && (
          <BuyItNowCardDetails
            onBuyNowClick={onBuyNowClick}
            onMakeOfferClick={onMakeOfferClick}
            status={status}
            directOfferExists={directOfferExists}
            buyNowListingInfo={buyNowListingInfo}
          />
        )}

        <div className='flex justifyBetween alignCenter'>
          {sellerInfo.isDealer && <VerifiedDealerOption />}
          <HistoryReport />
        </div>

        {isAuthenticated && sellerInfo.isDealer && <MembershipInfo />}

        <Seller isUserOwner={isUserOwner} />

        {directOfferExists && status !== OfferStatus.Cancelled && <OfferMadeLabel />}

        {auctionInfo?.reserveIsMet && auctionInfo?.currentUserIsTopBidder && (
          <ContinueButton auctionId={vehicleData?.auctionId} />
        )}

        {isAuthenticated && auctionInfo && (
          <BidInput shipmentRate={shipmentRate} isDealerVehicle={sellerInfo.isDealer} />
        )}

        {isDealerInventoryVehicleType && <DealerInventoryTypeMark />}
      </div>

      <style jsx>{`
        .root {
          position: sticky;
          top: 12rem;
          float: right;
          z-index: 20;
          max-width: 44.4rem;
          width: 100%;
          min-width: 33.2rem;
          margin: -10rem 0 0;
          padding: 0.3rem 0.5rem 0.9rem 0.6rem;
          overflow-x: hidden;
          overflow-y: scroll;
          max-height: 650px;
          -ms-overflow-style: none;
          scrollbar-width: none;

          .shadowContainer {
            position: relative;
            padding: 1.6rem 1.2rem 3.5rem;
            border-radius: 1rem;
            background: var(--secondary-bg-color);
            box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
          }
        }

        .root::-webkit-scrollbar {
          display: none;
        }

        @media (min-width: 1365px) and (max-width: 1488px) {
          .root {
            top: 10rem;
            margin: 1rem 0 0;
          }
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .root {
            top: 1.5rem;
            max-width: 33rem;
          }
        }
      `}</style>
    </div>
  )
}

const VehicleDetailsSideCardMobile = ({
  auctionInfo,
  directOfferExists,
  status,
  buyNowListingInfo,
  vehicleData,
  isAuthenticated,
  onBuyNowClick,
  onMakeOfferClick,
  isDealerInventoryVehicleType,
  sellerInfo,
  shipmentRate,
  isUserOwner,
}) => {
  const [memberships] = useMemberships([])
  return (
    <>
      <Deals />

      <div className='flex justifyBetween alignCenter'>
        {sellerInfo.isDealer && <VerifiedDealerOption />}
        <HistoryReport />
      </div>
      {memberships?.memberships?.length !== 0 ? (
        <div className='membershipInfo'>
          {isAuthenticated && sellerInfo.isDealer && <MembershipInfo />}
        </div>
      ) : null}

      <Seller isUserOwner={isUserOwner} />

      <div className='root flex alignCenter'>
        <BidderBadge />

        {auctionInfo && <AuctionInfo />}

        {directOfferExists && status !== OfferStatus.Cancelled && <OfferMadeLabel />}

        {buyNowListingInfo && vehicleData.status !== VehicleStatus.CompletedFromBuyNow && (
          <BuyNowInfo
            onBuyNowClick={onBuyNowClick}
            onMakeOfferClick={onMakeOfferClick}
            status={status}
            directOfferExists={directOfferExists}
          />
        )}

        {isAuthenticated && auctionInfo && (
          <BidInput shipmentRate={shipmentRate} isDealerVehicle={sellerInfo.isDealer} />
        )}

        {auctionInfo?.reserveIsMet && auctionInfo?.currentUserIsTopBidder && (
          <ContinueButton auctionId={vehicleData?.auctionId} />
        )}
      </div>

      {isDealerInventoryVehicleType && <DealerInventoryTypeMark />}

      <style jsx>{`
        .root {
          position: fixed;
          bottom: 0;
          left: 0;
          width: 100vw;
          padding: 2.7rem 0.9rem 2.9rem;
          background: #fff;

          :global(button) {
            width: 10.6rem;
          }

          :global(.bidderBadge) {
            position: absolute;
            top: -3.3rem;
            left: 50%;
            transform: translateX(-50%);
          }
        }
        @media (max-width: 1024px) {
          .membershipInfo {
            padding: 1.5rem 0rem;
            border-top: 0.1rem solid var(--separator-line-color);
          }
        }
      `}</style>

      <style jsx>{`
        .root {
          z-index: ${zIndex.Fixed};
        }
      `}</style>
    </>
  )
}

// ----------------------------------------------------------------------

export default function Vehicle() {
  const [openModal, setOpenModal] = useState(MODALS.None)
  const [target, setTarget] = useState(null)
  const sectionRefsForNavigation = useRef({})
  const [{ isAuthenticated, userDetails }] = useAuth()
  const {
    vehicleData,
    auctionInfo,
    buyNowListingInfo,
    galleryImages,
    sellerInfo,
    shipmentRate,
    queryLoadingStatuses: { isVehicleDataLoading },
  } = useVehiclePageData()

  const isUserOwner = vehicleData?.sellerId === userDetails.userId

  const {
    data: { directOfferExists, directOfferId, status } = {},
    refetch: refetchNegotiationInfo,
  } = useNegotiationInfo({
    forBuyer: true,
    vehicleId: vehicleData?.id,
    buyNowListingId: vehicleData?.buyNowListingId,
  })

  const redirectWinnerToPurchase = useCallback(() => {
    if (auctionInfo.auctionStatus === AuctionStatus.Ended && auctionInfo.currentUserIsTopBidder) {
      Router.push(`${ROUTES.PurchaseAuction}/${vehicleData?.auctionId}`)
    }
  }, [auctionInfo?.auctionStatus, auctionInfo?.currentUserIsTopBidder, vehicleData?.auctionId])

  useSignalr(vehicleData?.id, vehicleData?.listingType, redirectWinnerToPurchase)

  const handleMakeOfferClick = () => {
    try {
      //this can throw an exception when blocked by adblockers
      window.gtag('event', 'conversion', { send_to: GA_TRACKING_ID2 + '/eU34CL3CoooCELuM0rsB' })
    } catch {}

    setTarget(TARGETS.Offer)

    if (buyNowListingInfo.currentUserIsSeller) {
      setOpenModal(MODALS.CannotBuyYourOwnVehicle)
    } else if (!userDetails.verified) {
      setOpenModal(MODALS.GetVerified)
    } else {
      if (directOfferExists) setOpenModal(MODALS.NegotiationModal)
      else {
        setOpenModal(MODALS.BuyFeeAgreement)
      }
    }
  }

  const handleBuyItNowClick = () => {
    try {
      //this can throw an exception when blocked by adblockers
      window.gtag('event', 'conversion', { send_to: GA_TRACKING_ID2 + '/eU34CL3CoooCELuM0rsB' })
    } catch {}

    setTarget(TARGETS.BuyNow)

    if (buyNowListingInfo.currentUserIsSeller) {
      setOpenModal(MODALS.CannotBuyYourOwnVehicle)
    } else if (!userDetails.verified) {
      setOpenModal(MODALS.GetVerified)
    } else {
      setOpenModal(MODALS.BuyFeeAgreement)
    }
  }

  const handleBuyFeeAgreementAccept = () =>
    target === TARGETS.BuyNow
      ? setOpenModal(MODALS.BuyNowModal)
      : setOpenModal(MODALS.MakeAnOfferModal)

  const { data: conciergeLocations = [], isFetching: conciergeLocationsFetching } = useQuery({
    queryKey: getEnabledConciergeLocationsKey,
    queryFn: getEnabledConciergeLocations,
    keepPreviousData: true,
  })

  const sellerConciergeLocation = useMemo(() => {
    if (!!conciergeLocationsFetching) {
      console.log('conciergeLocations fetching')
      return null
    }

    if (!conciergeLocations) {
      console.log('conciergeLocations is null')
      return null
    }
    if (conciergeLocations.length == 0) {
      console.log('conciergeLocations is empty')
      return null
    }

    if (isVehicleDataLoading) {
      console.log('vehicleData is loading')
      return null
    }
    if (!vehicleData) {
      console.log('vehicleData is empty')
      return null
    }

    let sellerLocation = !vehicleData.location
      ? null
      : { lat: vehicleData.location.latitude, lng: vehicleData.location.longitude }

    let currentLocation = getClosestLocation(conciergeLocations, sellerLocation, 'springdale')
    if (!currentLocation) {
      console.log('defaulting to first location in list')
      currentLocation = conciergeLocations[0] // should be springdale
    }

    return currentLocation
  }, [conciergeLocations, vehicleData, isVehicleDataLoading, conciergeLocationsFetching])

  if (isVehicleDataLoading) return <Loader delay={300} fullPage />

  if (!vehicleData) return <ErrorComponent statusCode={404} />

  const vehicleDetailsCardProps = {
    isAuthenticated,
    sellerConciergeLocation,
    auctionInfo,
    buyNowListingInfo,
    vehicleData,
    onBuyNowClick: handleBuyItNowClick,
    onMakeOfferClick: handleMakeOfferClick,
    status,
    directOfferExists,
    isDealerInventoryVehicleType:
      vehicleData?.vehicleType === VehicleType.DealerInventory ||
      vehicleData?.vehicleType === VehicleType.DealerInventoryManualEntry,
    sellerInfo,
    shipmentRate,
    isUserOwner,
  }

  const shouldShowOwnershipSection = vehicleData?.vehicleType !== VehicleType.DealerInventory
  const shouldShowInspectionDetailsSection =
    vehicleData?.vehicleType !== VehicleType.DealerInventory

  const vehicleDescriptionHtml = vehicleData?.description ? wrapURLs(vehicleData.description) : ''

  return (
    <>
      <Head>
        <title>{`${vehicleData?.make} ${vehicleData?.model} ${vehicleData?.trim} for Sale`}</title>
        <meta name='description' content='description' />
      </Head>

      <Desktop>
        <Navbar nodesToObserve={sectionRefsForNavigation} />
      </Desktop>

      <Container>
        <Mobile>
          <button className='flatBtn backBtn' onClick={Router.back}>
            {'< Back to Listings'}
          </button>
          <LotInfo sellerConciergeLocation={sellerConciergeLocation} shipmentRate={shipmentRate} />
        </Mobile>

        <Desktop>
          <VehicleDetailsSideCardDesktop {...vehicleDetailsCardProps} />
        </Desktop>

        <section
          id='gallery'
          ref={(ref) =>
            (sectionRefsForNavigation.current['gallery'] = { ref, displayName: 'Photos' })
          }
        >
          <Desktop>
            <h1 className='vehicleHeading flex justifyBetween'>
              {`${vehicleData?.year} ${vehicleData?.make} ${vehicleData?.model.replace(
                '-',
                '\u2011',
              )}`}{' '}
              {/* regular hypens are replaced with non-breaking hyphens to avoid line breaks in vehicle models like "F-150"*/}
              <span style={{ flexShrink: 0 }}>{`${vehicleData?.mileage.toLocaleString()} mi`}</span>
            </h1>
            <p className='trim'>{vehicleData.trim}</p>
          </Desktop>

          <div className='galleryWrapper'>
            {auctionInfo &&
              [AuctionStatus.Ended, AuctionStatus.Stopped].includes(auctionInfo.auctionStatus) && (
                <AuctionStatusBadge
                  className='auctionStatusBadge'
                  status={auctionInfo.auctionStatus}
                  reserveIsMet={auctionInfo.reserveIsMet}
                />
              )}
            {vehicleData.status === VehicleStatus.CompletedFromBuyNow && (
              <AuctionStatusBadge
                className='auctionStatusBadge'
                status={vehicleData.status}
                reserveIsMet={vehicleData.sellDetails?.soldAt}
              />
            )}
            <Gallery
              images={galleryImages}
              isDealer={
                vehicleData?.vehicleType === VehicleType.DealerInventory ||
                vehicleData?.vehicleType === VehicleType.DealerInventoryManualEntry
              }
            />
          </div>
        </section>

        <Mobile>
          <VehicleDetailsSideCardMobile {...vehicleDetailsCardProps} />
        </Mobile>

        {vehicleData?.description && (
          <section className='sellerComments'>
            <h2>Vehicle Description</h2>
            <p dangerouslySetInnerHTML={{ __html: vehicleDescriptionHtml }} />
          </section>
        )}

        {shouldShowOwnershipSection && (
          <section id='ownership-details'>
            <Ownership />
          </section>
        )}

        <section
          id='details'
          ref={(ref) =>
            (sectionRefsForNavigation.current['details'] = { ref, displayName: 'Vehicle Details' })
          }
        >
          <Details />
        </section>

        {shouldShowInspectionDetailsSection && (
          <section
            id='inspection-details'
            ref={(ref) =>
              (sectionRefsForNavigation.current['inspection-details'] = {
                ref,
                displayName: 'Inspection',
              })
            }
          >
            <Inspection />
          </section>
        )}

        {!!sellerConciergeLocation && (
          <section>
            <Desktop>
              <div className='mapsWrapper'>
                <h3 className='locationHeading'>privauto Concierge Location</h3>
                <h4>
                  <Link key={sellerConciergeLocation.value} href={sellerConciergeLocation.href}>
                    {sellerConciergeLocation.address3 + ', ' + sellerConciergeLocation.label}
                  </Link>
                </h4>
                <Location
                  lat={sellerConciergeLocation.lat}
                  lng={sellerConciergeLocation.lng}
                  title={sellerConciergeLocation.address3 + ', ' + sellerConciergeLocation.label}
                />
              </div>
            </Desktop>
            <Mobile>
              <h3 className='locationHeading'>privauto Concierge Location</h3>
              <h4>
                <Link key={sellerConciergeLocation.value} href={sellerConciergeLocation.href}>
                  {sellerConciergeLocation.address3 + ', ' + sellerConciergeLocation.label}
                </Link>
              </h4>
              <Location
                lat={sellerConciergeLocation.lat}
                lng={sellerConciergeLocation.lng}
                title={sellerConciergeLocation.address3 + ', ' + sellerConciergeLocation.label}
              />
            </Mobile>
          </section>
        )}

        <section
          id='payment'
          ref={(ref) =>
            (sectionRefsForNavigation.current['payment'] = { ref, displayName: 'Payment' })
          }
        >
          <Payments />
        </section>

        <section>
          <CTACards />
        </section>
      </Container>

      <script
        dangerouslySetInnerHTML={{
          __html: `setTimeout(function(){
	        try{
	            var sa_image = document.querySelector('div.mainImgWrapper div.active img').src;
	            var sa_name = document.querySelector('div.vehicle strong').innerText;
	            var sa_price = document.querySelector('div.priceRibbon').innerText;
	
	            if( !!sa_name && !!sa_image && !!sa_price  ) {
	                !function(s,a,e,v,n,t,z){if(s.saq)return;n=s.saq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!s._saq)s._saq=n;n.push=n;n.loaded=!0;n.version='1.0';n.queue=[];t=a.createElement(e);t.async=!0;t.src=v;z=a.getElementsByTagName(e)[0];z.parentNode.insertBefore(t,z)}(window,document,'script','https://tags.srv.stackadapt.com/events.js');saq('drt','oQS4KVqZ_Q7RZ37IOoRyqw', { 'product': sa_name, 'img_url': sa_image, 'price': sa_price, 'use_landing': true});
	            }
	
	        } catch(e) {
	            console.log(e);    
	        }
	    }, 4000);`,
        }}
      />

      {openModal === MODALS.NegotiationModal && (
        <NegotiationModal
          forBuyer
          vehicleId={vehicleData.id}
          directOfferId={directOfferId}
          userId={vehicleData.sellerId}
          buyNowListingId={vehicleData.buyNowListingId}
          isDisabled={vehicleData.status === VehicleStatus.CompletedFromBuyNow}
          onClose={() => setOpenModal(MODALS.None)}
        />
      )}

      {openModal === MODALS.MakeAnOfferModal &&
        differenceInSeconds(parseISO(buyNowListingInfo.expirationDate, { withTime: true }), now()) >
          0 && (
          <MakeAnOfferModal
            vehicleId={vehicleData.id}
            buyNowListingId={vehicleData.buyNowListingId}
            minPrice={buyNowListingInfo.minimumOfferAmount}
            buyNowPrice={vehicleData.price}
            onSuccess={() => setOpenModal(MODALS.OfferReceived)}
            onClose={() => setOpenModal(MODALS.None)}
            shipmentRate={shipmentRate}
          />
        )}

      {openModal === MODALS.OfferReceived && (
        <OfferReceivedModal
          onClose={() => {
            refetchNegotiationInfo()
            setOpenModal(MODALS.None)
          }}
        />
      )}

      {openModal === MODALS.BuyNowModal && (
        <BuyNowModal
          vehicleId={vehicleData?.id}
          buyNowListingId={vehicleData?.buyNowListingId}
          onClose={() => setOpenModal(MODALS.None)}
          vehicleName={`${vehicleData?.year} ${vehicleData?.make} ${vehicleData?.model}`}
          price={vehicleData.desiredBuyNowPrice}
          shipmentRate={shipmentRate}
        />
      )}

      {openModal === MODALS.CannotBuyYourOwnVehicle && (
        <CannotBuyYourVehicle target={target} onClose={() => setOpenModal(MODALS.None)} />
      )}

      {openModal === MODALS.GetVerified && (
        <GetVerified target={target} onClose={() => setOpenModal(MODALS.None)} />
      )}

      {openModal === MODALS.BuyFeeAgreement && (
        <BuyFeeAgreement
          target={target}
          onClose={() => setOpenModal(MODALS.None)}
          onAccept={handleBuyFeeAgreementAccept}
          buyFee={process.env.NEXT_PUBLIC_BUY_FEE}
          shipmentRate={shipmentRate}
          isDealerVehicle={sellerInfo.isDealer}
        />
      )}

      <style jsx>{`
        :global(header) {
          position: relative !important;
        }

        section {
          max-width: calc(100% - 48.1rem);

          &#gallery {
            .galleryWrapper {
              position: relative;
            }

            :global(.auctionStatusBadge) {
              position: absolute;
              top: 0;
              z-index: 2;
            }
          }

          &#inspection-details,
          &#ownership-details {
            position: relative;
            margin: 11rem 0 0;

            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: calc((100vw - (100% + 34.5rem)) / -2);
              width: 100vw;
              height: 100%;
              background: #f5f6f9;
              z-index: -1;
            }
          }

          &#ownership-details {
            margin: 5rem 0 0;
          }
        }

        .vehicleHeading {
          font-weight: 700;
          font-size: 4.4rem;
          line-height: 5.8rem;
          gap: 2rem;
        }

        .trim {
          color: var(--secondary-text-color);
          font-size: 1.4rem;
          margin-bottom: 1rem;
        }

        .mapsWrapper {
          margin: 5.85rem 0 0;
          border-radius: 1rem;
          overflow: hidden;
        }

        .locationHeading {
          margin: 0 0 1rem;
          font-size: 2rem;
        }

        .backBtn {
          margin: 1.7rem 0 0;
          color: var(--secondary-text-color);
          font-size: 1.2rem;
          font-weight: 700;
        }

        .sellerComments {
          max-width: calc(100% - 48.1rem);

          h2 {
            margin: 5.4rem 0 1.5rem;
            font-size: 3.2rem;
          }

          p {
            font-size: 1.8rem;

            :global(a) {
              color: var(--primary-color);
            }
          }
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          section {
            max-width: calc(100% - (33rem + 2.7rem));

            &#inspection-details {
              margin: 3.7rem 0 0;
            }
          }

          .vehicleHeading {
            margin: 2rem 0 0.5rem;
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .trim {
            color: var(--secondary-text-color);
            font-size: 1.2rem;
            margin-bottom: 1rem;
          }

          .vehicleDescription {
            h2 {
              margin: 2.6rem 0 0.7rem;
              font-size: 2rem;
            }

            p {
              line-height: 1.8rem;
              font-size: 1.4rem;
            }
          }

          .mapsWrapper {
            margin: 3.3rem 0 0;
          }
        }

        @media (max-width: 1024px) {
          :global(header) {
            position: sticky !important;
          }

          .container {
            padding: 0 1.2rem;
          }

          section {
            max-width: 100%;
            scroll-margin-top: initial;

            &#inspection-details,
            &#ownership-details {
              margin: 3rem 0 0;

              &::before {
                left: -1.2rem;
                width: calc(100% + 2.4rem);
              }
            }
          }

          .locationHeading {
            margin: 0 0 1rem 2rem;
            font-size: 1.5rem;
          }

          .sellerComments {
            max-width: 100%;

            h2 {
              margin: 2.7rem 0 0.8rem;
              font-size: 2rem;
            }

            p {
              font-size: 1.4rem;
            }
          }
        }
      `}</style>
    </>
  )
}

Vehicle.getInitialProps = () => ({ namespacesRequired: ['common'] })
